import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  fetchBrochuresByIctId,
  insertBrochure,
  updateBrochure,
  patchBrochureStatus,
  deleteBrochure,
} from "../../../../../store/ictBrochureSlice";

const IctBrochure = ({ ictId, onClose, showInsertForm = false }) => {
  const dispatch = useDispatch();
  const { brochures = [], loading, error } = useSelector((s) => s.ictBrochure || {});
  const sanitizeUrl = (url) => url.replace(/([^:])\/\//g, "$1/");

  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("A");

  const [editId, setEditId] = useState(null);
  const [editStatus, setEditStatus] = useState("A");
  const [editFile, setEditFile] = useState(null);

  useEffect(() => {
    if (ictId && !showInsertForm) {
      dispatch(fetchBrochuresByIctId(ictId));
    }
  }, [dispatch, ictId, showInsertForm]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === "string" ? error : JSON.stringify(error), {
        toastId: "brochure-err",
      });
    }
  }, [error]);

  const handleInsert = async (e) => {
    e.preventDefault();

    if (!ictId) return toast.error("ictId missing from URL.");
    if (!file) return toast.error("Please select a brochure file.");

    try {
      await dispatch(
        insertBrochure({
          ictId: Number(ictId),        // ✅ from URL
          brochureFile: file,          // ✅ file
          status,
        })
      ).unwrap();

      toast.success("Brochure uploaded successfully!");
      setFile(null);
      setStatus("A");
      onClose?.();
    } catch (err) {
      toast.error("Insert failed.");
      console.error(err);
    }
  };

  const startEdit = (b) => {
    setEditId(b.id);
    setEditStatus(b.status || "A");
    setEditFile(null);
  };

  const saveEdit = async () => {
    try {
      await dispatch(
        updateBrochure({
          id: editId,
          status: editStatus,
          brochureFile: editFile,
        })
      ).unwrap();

      toast.success("Updated successfully!");
      setEditId(null);
      setEditFile(null);
      setEditStatus("A");
    } catch (err) {
      toast.error("Update failed.");
      console.error(err);
    }
  };

  const toggleStatus = async (b) => {
    const newStatus = b.status === "A" ? "D" : "A";
    try {
      await dispatch(patchBrochureStatus({ id: b.id, status: newStatus })).unwrap();
      toast.success(newStatus === "A" ? "Activated" : "Deactivated");
    } catch (err) {
      toast.error("Status change failed.");
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this brochure?")) return;
    try {
      await dispatch(deleteBrochure(id)).unwrap();
      toast.success("Deleted successfully!");
    } catch (err) {
      toast.error("Delete failed.");
      console.error(err);
    }
  };

  return (
    <div className="popup-overlay">
      <ToastContainer position="bottom-right" autoClose={2500} />

      <div className="popup-content large">
        <div className="popup-header">
          <h3>
            {showInsertForm ? "Upload ICT Brochure" : `Brochures for ICT ID: ${ictId}`}
            <button className="close-btn" onClick={onClose}>
              <i className="fa fa-times"></i>
            </button>
          </h3>
        </div>

        {showInsertForm ? (
          <form onSubmit={handleInsert}>
            <div className="form-group">
              <label>Brochure File (pdf/png/jpg/jpeg)</label>
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                required
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="A">Active</option>
                <option value="D">Disabled</option>
              </select>
            </div>

            <div className="form-actions">
              <button type="submit">
                <i className="fa fa-plus-square" />
              </button>
              <button type="button" onClick={onClose}>
                <i className="fa fa-close" />
              </button>
            </div>
          </form>
        ) : (
          <>
            {loading && <p>Loading brochures...</p>}

            {!brochures.length ? (
              <p>No brochures found.</p>
            ) : (
              <table className="table styled-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Brochure</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {brochures.map((b, idx) => (
                    <tr key={b.id}>
                      <td>{idx + 1}</td>

                      <td>
                        {b.brochure ? (
                          <a
                            href={sanitizeUrl(
                              `https://iteyonikservicesinstitute.com/${String(b.brochure).replace(/^\//, "")}`
                            )}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {String(b.brochure).split("/").pop()}
                          </a>
                        ) : (
                          "—"
                        )}

                        {editId === b.id && (
                          <div style={{ marginTop: 8 }}>
                            <input
                              type="file"
                              accept=".pdf,image/*"
                              onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                            />
                          </div>
                        )}
                      </td>

                      <td>
                        {editId === b.id ? (
                          <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                            <option value="A">Active</option>
                            <option value="D">Disabled</option>
                          </select>
                        ) : (
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={b.status === "A"}
                              onChange={() => toggleStatus(b)}
                            />
                            <span className="slider round" />
                          </label>
                        )}
                      </td>

                      <td>{b.created ? new Date(b.created).toLocaleString() : "-"}</td>

                      <td>
                        {editId === b.id ? (
                          <button className="icon-btn update-btn fa fa-save" onClick={saveEdit} />
                        ) : (
                          <button className="icon-btn update-btn fa fa-edit" onClick={() => startEdit(b)} />
                        )}

                        <button
                          className="icon-btn delete-btn fa fa-trash"
                          onClick={() => handleDelete(b.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default IctBrochure;
